import { 
  MonthlyPayrollData, 
  PayrollSession, 
  DataSourceType,
  AnnualTotals,
  SalarySlipData
} from '../types/PayrollTypes';

export class PayrollEstimation {
  
  // デフォルトの月給データを作成
  private static getDefaultMonthlyData(year: number, month: number): MonthlyPayrollData {
    return {
      year,
      month,
      isBonus: false,
      grossSalary: 0,
      healthInsurance: 0,
      employmentInsurance: 0,
      nationalPension: 0,
      incomeTax: 0,
      localTax: 0,
      definedContribution: 0,
      sourceType: DataSourceType.ESTIMATED,
      lastUpdated: new Date().toISOString()
    };
  }
  
  // デフォルトの賞与データを作成
  private static getDefaultBonusData(year: number): MonthlyPayrollData {
    return {
      year,
      month: 0, // 賞与は月を0とする
      isBonus: true,
      grossSalary: 0,
      healthInsurance: 0,
      employmentInsurance: 0,
      nationalPension: 0,
      incomeTax: 0,
      localTax: 0,
      definedContribution: 0,
      sourceType: DataSourceType.ESTIMATED,
      lastUpdated: new Date().toISOString()
    };
  }
  
  // 月給推定：最新のPDFデータまたは最も近い月のデータを使用
  static estimateMonthlySalary(
    existingData: MonthlyPayrollData[], 
    targetYear: number, 
    targetMonth: number
  ): MonthlyPayrollData {
    
    // 1. PDF_IMPORTEDデータを優先して取得（日付の近い順）
    const pdfImportedData = existingData
      .filter(data => !data.isBonus && data.sourceType === DataSourceType.PDF_IMPORTED)
      .sort((a, b) => {
        const targetDate = targetYear * 12 + targetMonth;
        const aDate = a.year * 12 + a.month;
        const bDate = b.year * 12 + b.month;
        return Math.abs(aDate - targetDate) - Math.abs(bDate - targetDate);
      });
    
    // 2. PDFデータがなければ、USER_EDITEDデータを使用
    const userEditedData = existingData
      .filter(data => !data.isBonus && data.sourceType === DataSourceType.USER_EDITED)
      .sort((a, b) => {
        const targetDate = targetYear * 12 + targetMonth;
        const aDate = a.year * 12 + a.month;
        const bDate = b.year * 12 + b.month;
        return Math.abs(aDate - targetDate) - Math.abs(bDate - targetDate);
      });
    
    // 3. 基準となるデータを選択
    const baseData = pdfImportedData[0] || userEditedData[0];
    
    if (!baseData) {
      return this.getDefaultMonthlyData(targetYear, targetMonth);
    }
    
    return {
      ...baseData,
      year: targetYear,
      month: targetMonth,
      isBonus: false,
      sourceType: DataSourceType.ESTIMATED,
      lastUpdated: new Date().toISOString()
    };
  }
  
  // 賞与推定：平均月収の1ヶ月分として計算
  static estimateBonus(monthlyData: MonthlyPayrollData[], targetYear: number): MonthlyPayrollData {
    const salaryData = monthlyData.filter(data => 
      !data.isBonus && 
      data.grossSalary > 0 &&
      (data.sourceType === DataSourceType.PDF_IMPORTED || data.sourceType === DataSourceType.USER_EDITED)
    );
    
    if (salaryData.length === 0) {
      return this.getDefaultBonusData(targetYear);
    }
    
    // 平均値を計算
    const calculateAverage = (field: keyof MonthlyPayrollData): number => {
      const sum = salaryData.reduce((acc, data) => {
        const value = data[field];
        return acc + (typeof value === 'number' ? value : 0);
      }, 0);
      return Math.round(sum / salaryData.length);
    };
    
    return {
      year: targetYear,
      month: 0,
      isBonus: true,
      grossSalary: calculateAverage('grossSalary'),
      healthInsurance: calculateAverage('healthInsurance'),
      employmentInsurance: calculateAverage('employmentInsurance'),
      nationalPension: calculateAverage('nationalPension'),
      incomeTax: 0, // 賞与の所得税・住民税は推定しない
      localTax: 0,
      definedContribution: calculateAverage('definedContribution'),
      sourceType: DataSourceType.ESTIMATED,
      lastUpdated: new Date().toISOString()
    };
  }
  
  // セッション全体の推定値更新
  static updateEstimations(session: PayrollSession): PayrollSession {
    const targetYear = session.currentYear;
    const updatedData = [...session.monthlyData];
    
    // 1-12月の推定値更新
    for (let month = 1; month <= 12; month++) {
      const existingIndex = updatedData.findIndex(data => 
        data.year === targetYear && data.month === month && !data.isBonus
      );
      
      // 推定値のみ更新（PDF_IMPORTEDやUSER_EDITEDは保持）
      if (existingIndex === -1 || updatedData[existingIndex].sourceType === DataSourceType.ESTIMATED) {
        const estimated = this.estimateMonthlySalary(updatedData, targetYear, month);
        if (existingIndex === -1) {
          updatedData.push(estimated);
        } else {
          updatedData[existingIndex] = estimated;
        }
      }
    }
    
    // 賞与の自動推定は削除（手動でのみ追加）
    
    return {
      ...session,
      monthlyData: updatedData,
      lastUpdated: new Date().toISOString()
    };
  }
  
  // 年間合計を計算
  static calculateAnnualTotals(monthlyData: MonthlyPayrollData[], year: number): AnnualTotals {
    const targetYear = year;
    const yearData = monthlyData.filter(data => data.year === targetYear);
    
    const totals = yearData.reduce((acc, data) => ({
      totalGrossSalary: acc.totalGrossSalary + data.grossSalary,
      totalHealthInsurance: acc.totalHealthInsurance + data.healthInsurance,
      totalEmploymentInsurance: acc.totalEmploymentInsurance + data.employmentInsurance,
      totalNationalPension: acc.totalNationalPension + data.nationalPension,
      totalIncomeTax: acc.totalIncomeTax + data.incomeTax,
      totalLocalTax: acc.totalLocalTax + data.localTax,
      totalDefinedContribution: acc.totalDefinedContribution + data.definedContribution,
    }), {
      totalGrossSalary: 0,
      totalHealthInsurance: 0,
      totalEmploymentInsurance: 0,
      totalNationalPension: 0,
      totalIncomeTax: 0,
      totalLocalTax: 0,
      totalDefinedContribution: 0,
    });
    
    return totals;
  }
  
  // PDFデータから月別データを作成
  static createMonthlyDataFromPdf(pdfData: SalarySlipData): MonthlyPayrollData {
    // 日付が不正・欠落の場合は現在日時で補完する（解析は例外にしない方針）
    const parsed = pdfData.salaryDate ? new Date(pdfData.salaryDate) : null;
    const salaryDate = parsed && !isNaN(parsed.getTime()) ? parsed : new Date();
    const isBonus = pdfData.isBonus || false;
    
    return {
      year: salaryDate.getFullYear(),
      month: isBonus ? salaryDate.getMonth() + 1 : salaryDate.getMonth() + 1, // 賞与の場合も実際の月を保持
      isBonus: isBonus,
      grossSalary: pdfData.grossSalary || 0,
      healthInsurance: pdfData.healthInsurance || 0,
      employmentInsurance: pdfData.employmentInsurance || 0,
      nationalPension: pdfData.nationalPension || 0,
      incomeTax: pdfData.incomeTax || 0,
      localTax: pdfData.localTax || 0,
      definedContribution: pdfData.definedContribution || 0,
      sourceType: DataSourceType.PDF_IMPORTED,
      lastUpdated: new Date().toISOString()
    };
  }
}